export type TimelineUtterance = {
  startMs: number;
  speaker: string;
  text: string;
};

export type TimelineTranscriptSnippet = {
  /** Agent turn immediately before the focal user reply. */
  agent?: string;
  /** User turn at or nearest the playhead. */
  user?: string;
};

function speakerKey(speaker: string) {
  const s = String(speaker || "").toLowerCase();
  if (s.includes("user")) return "user";
  if (s.includes("agent") || s.includes("assistant")) return "agent";
  return s || "other";
}

type MergedTurn = { speaker: string; text: string; startMs: number; endMs: number };

/** Merge only back-to-back STT fragments (same speaker, short gap) — not separate turns minutes apart. */
const MERGE_GAP_MS = 3500;

function mergeSameSpeaker(rows: TimelineUtterance[]): MergedTurn[] {
  const out: MergedTurn[] = [];
  for (const row of rows) {
    const text = String(row.text || "").trim();
    if (!text) continue;
    const speaker = row.speaker || "Speaker";
    const prev = out[out.length - 1];
    if (
      prev
      && speakerKey(prev.speaker) === speakerKey(speaker)
      && row.startMs - prev.endMs <= MERGE_GAP_MS
    ) {
      prev.text = `${prev.text} ${text}`.replace(/\s+/g, " ").trim();
      prev.endMs = row.startMs;
    } else {
      out.push({ speaker, text, startMs: row.startMs, endMs: row.startMs });
    }
  }
  return out;
}

/**
 * Two-line context for Interaction timeline hover: prior agent prompt + user reply at the playhead.
 */
export function timelineTranscriptSnippet(
  utterances: TimelineUtterance[],
  atSec: number,
  durationSec: number,
): TimelineTranscriptSnippet {
  const sorted = [...utterances]
    .filter((u) => String(u.text || "").trim())
    .sort((a, b) => a.startMs - b.startMs);
  if (!sorted.length) return {};

  const turns = mergeSameSpeaker(sorted);
  if (!turns.length) return {};

  const atMs = Math.max(0, atSec * 1000);
  const durMs = Math.max(1000, durationSec * 1000);

  let focalIdx = 0;
  let best = Infinity;
  for (let i = 0; i < turns.length; i += 1) {
    const nextMs = i + 1 < turns.length ? turns[i + 1].startMs : durMs;
    const mid = atMs >= turns[i].startMs && atMs < nextMs
      ? 0
      : Math.min(Math.abs(turns[i].startMs - atMs), Math.abs(nextMs - atMs));
    if (mid < best) {
      best = mid;
      focalIdx = i;
    }
  }

  let userIdx = speakerKey(turns[focalIdx].speaker) === "user"
    ? focalIdx
    : -1;

  if (userIdx < 0) {
    for (let i = focalIdx; i < turns.length; i += 1) {
      if (speakerKey(turns[i].speaker) === "user") {
        userIdx = i;
        break;
      }
    }
  }
  if (userIdx < 0) {
    for (let i = focalIdx; i >= 0; i -= 1) {
      if (speakerKey(turns[i].speaker) === "user") {
        userIdx = i;
        break;
      }
    }
  }

  if (userIdx < 0) {
    if (speakerKey(turns[focalIdx].speaker) === "agent") {
      return { agent: turns[focalIdx].text };
    }
    return {};
  }

  let agent: string | undefined;
  for (let j = userIdx - 1; j >= 0; j -= 1) {
    if (speakerKey(turns[j].speaker) === "agent") {
      agent = turns[j].text;
      break;
    }
  }

  return { agent, user: turns[userIdx].text };
}
